import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Icon, SimpleModal, toast } from '@livable/saas-react-components';
import { Button, LinearProgress, TextField, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { USER_SETTINGS_KEYS } from 'src/constants/userSetting';
import { useStore } from 'src/stores';
import { ACCEPTED_BILL_FILE_TYPES, BULK_UPLOAD_STATUS, MAX_BULK_UPLOAD_FILES, MAX_BULK_UPLOAD_FILE_MB } from 'src/stores/billAutomation/bulkUpload';
import { getPropertyAddressIndex, getScheduledMonth, mapCompanyBillToRow } from './companyBillRows';
import useStyles from './uploadBillModalStyles';
import { useUploadStatusPoll } from './useUploadStatusPoll';
const CHIP = {
  extracting: {
    label: 'Extracting…',
    tone: 'info'
  },
  processed: {
    label: 'Processed',
    tone: 'success'
  },
  needsReview: {
    label: 'Needs review',
    tone: 'warning'
  },
  error: {
    label: 'Error',
    tone: 'error'
  },
  duplicate: {
    label: 'Already uploaded',
    tone: 'warning'
  }
};
const formatMB = mb => "".concat(mb.toFixed(1), " MB");

/** Long enough for what a person writes to the team about a bill; the service keeps no more. */
export const MAX_BILL_NOTE_LENGTH = 2000;

/**
 * What a landed file's row says, read from the bills list — the same rows the table behind renders,
 * mapped through the same mapper. A bill's progress is the bill's own, so reading it from anywhere
 * else is how the modal ends up insisting a file is still pending while the table has it validated.
 */
const describeLandedBill = (row, addressById) => {
  if (!row) return {
    chip: CHIP.extracting,
    detail: 'uploaded'
  };
  const mapped = mapCompanyBillToRow(row, addressById);
  const address = (mapped.propertiesLines || [])[0];
  const utilities = (mapped.utilityTypes || []).join(', ');
  switch (row.extractStatus) {
    case 'Validated':
      {
        // Scheduled for a later month (LB-6009): processed, and waiting for that month's table.
        const attached = address ? "attached to ".concat(address).concat(utilities ? " \xB7 ".concat(utilities) : '') : 'processed';
        const scheduledMonth = (row.utilities || []).map(utility => getScheduledMonth(utility)).find(Boolean);
        if (scheduledMonth) {
          return {
            chip: CHIP.processed,
            detail: "".concat(attached, " \xB7 scheduled for ").concat(scheduledMonth, ": it's offered then"),
            isScheduled: true
          };
        }
        return {
          chip: CHIP.processed,
          detail: attached
        };
      }
    case 'NeedsProperty':
      // The list carries no reason, so it says what to do rather than guessing why.
      return {
        chip: CHIP.needsReview,
        detail: 'no property matched — open the bill to pick one'
      };
    case 'Failed':
      return {
        chip: CHIP.error,
        detail: 'extraction failed — open the bill for details'
      };
    case 'Duplicate':
      // Server-side dedup after landing (same account + period, LB-5779); the tooltip carries its reason.
      return {
        chip: CHIP.duplicate,
        detail: mapped.statusTooltip || 'already received for this company',
        originalBillId: row.duplicateOfBillId || ''
      };
    case 'Rejected':
      return {
        chip: CHIP.error,
        detail: mapped.statusTooltip || 'rejected — open the bill for details'
      };
    default:
      return {
        chip: CHIP.extracting,
        detail: 'uploaded'
      };
  }
};

/** What one queue row shows: a chip and a line under the name, or a progress bar while it is going up. */
const describeEntry = (entry, billRow, addressById) => {
  switch (entry.status) {
    case BULK_UPLOAD_STATUS.uploading:
      return {
        isUploading: true,
        detail: null,
        chip: null
      };
    case BULK_UPLOAD_STATUS.duplicate:
      return {
        chip: CHIP.duplicate,
        detail: 'already uploaded for this company',
        originalBillId: entry.duplicateOfBillId
      };
    case BULK_UPLOAD_STATUS.error:
      return {
        chip: CHIP.error,
        detail: entry.error
      };
    case BULK_UPLOAD_STATUS.landed:
      return describeLandedBill(billRow, addressById);
    default:
      return {
        chip: null,
        detail: 'waiting'
      };
  }
};
const countOf = (n, one, many) => "".concat(n, " ").concat(n === 1 ? one : many);

/**
 * What Done says about the files in the queue (LB-6016). It used to close without a word, so a file the company already
 * had -- "Already uploaded" on a row nobody was looking at any more -- went by unremarked. Null with nothing to report.
 *
 * @param described each entry as {@link describeEntry} describes it, with the entry itself
 */
export const summarizeUploadQueue = described => {
  if (!described.length) return null;
  const count = chip => described.filter(row => row.chip === chip).length;
  const processed = count(CHIP.processed);
  const reading = count(CHIP.extracting);
  const needsReview = count(CHIP.needsReview);
  const duplicates = count(CHIP.duplicate);
  // An error chip is either a file that never went up, or a bill that did and could not be read or was held.
  const failed = described.filter(row => {
    var _row$entry;
    return row.chip === CHIP.error && ((_row$entry = row.entry) === null || _row$entry === void 0 ? void 0 : _row$entry.status) === BULK_UPLOAD_STATUS.error;
  }).length;
  const unprocessed = count(CHIP.error) - failed;
  const uploading = described.filter(row => row.isUploading || !row.chip).length;
  const scheduled = described.filter(row => row.isScheduled).length;
  const lines = [processed ? "".concat(processed, " processed") : null, scheduled ? "".concat(countOf(scheduled, 'is', 'are'), " scheduled for a later month's table") : null, reading ? "".concat(reading, " still being read") : null, uploading ? "".concat(uploading, " still uploading") : null, needsReview ? "".concat(countOf(needsReview, 'needs', 'need'), " review") : null, duplicates ? "".concat(countOf(duplicates, 'was', 'were'), " already on file, so ").concat(duplicates === 1 ? 'it wasn’t' : 'they weren’t', " added again") : null, unprocessed ? "".concat(unprocessed, " couldn\u2019t be processed: open ").concat(unprocessed === 1 ? 'it' : 'them', " for details") : null, failed ? "".concat(failed, " couldn\u2019t be uploaded") : null].filter(Boolean);
  if (reading || uploading) lines.push('They carry on in the background.');
  return {
    tone: duplicates || failed || unprocessed || needsReview ? 'warning' : 'success',
    title: countOf(described.length, 'file', 'files'),
    lines
  };
};

/**
 * Drops bill files in and watches them through extraction. Every file becomes its own bill and goes
 * up on its own request, so one slow or bad file holds up nothing else, and the queue lives in the
 * store rather than here — the modal can be closed while files are still going up.
 *
 * No property is chosen here. The pipeline matches a bill to its property from the service address
 * on the document; one it cannot place lands in review, which is a better answer than a guess made
 * at upload time by someone reading a file name.
 *
 * `isLocked` when the company's plan leaves uploading here out (LB-6233) after the dialog was opened: it takes no more
 * files, and still shows what became of those already sent.
 */
export const UploadBillModal = observer(_ref => {
  let isOpen = _ref.isOpen,
    handleClose = _ref.handleClose,
    onOpenBill = _ref.onOpenBill,
    _ref$isLocked = _ref.isLocked,
    isLocked = _ref$isLocked === void 0 ? false : _ref$isLocked;
  const classes = useStyles();
  const store = useStore();
  const fileInputRef = useRef(null);
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isDraggingOver = _useState2[0],
    setIsDraggingOver = _useState2[1];
  // For our team, sent with the files dropped next, then cleared (LB-6056). Offered only where the service keeps it.
  const _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    note = _useState4[0],
    setNote = _useState4[1];
  const isNoteOffered = store.account.featureFlagsStore.getIsBillNotesEnabled;
  const queue = store.bulkUpload;
  const companyName = localStorage.getItem(USER_SETTINGS_KEYS.companyName) || '';
  const addressById = getPropertyAddressIndex(store.company.propertiesList);
  useEffect(() => {
    if (isOpen) return;

    // What finished has been read; what has not is still going up and stays for the next open.
    queue.clearSettledEntries();
    setNote('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  const acceptFiles = fileList => {
    const files = Array.from(fileList || []).filter(file => file instanceof File);
    if (!files.length) return;
    const _queue$addFiles = queue.addFiles(files),
      added = _queue$addFiles.added,
      rejected = _queue$addFiles.rejected;
    if (rejected > 0) toast('warning', {
      body: ["Up to ".concat(MAX_BULK_UPLOAD_FILES, " files can be uploaded at once.")]
    });

    // Nothing to confirm — there is no per-file choice left to make, so they start straight away. The note is
    // whatever the field holds as they are dropped, so it is filled in first.
    queue.startProcessing(isNoteOffered ? note : '');
    // It went with those files; the next drop starts without it, as the queue sends it. Kept when no file was
    // taken, so it is not lost to a drop that added nothing.
    if (added > 0) setNote('');
  };
  const handleBrowse = event => {
    acceptFiles(event.target.files);
    // Lets the same file be picked twice in a row, which otherwise fires no change event.
    event.target.value = '';
  };
  const handleDrop = event => {
    var _event$dataTransfer;
    event.preventDefault();
    setIsDraggingOver(false);
    acceptFiles((_event$dataTransfer = event.dataTransfer) === null || _event$dataTransfer === void 0 ? void 0 : _event$dataTransfer.files);
  };
  const entries = [...queue.entries];
  useUploadStatusPoll({
    isOpen,
    billIds: entries.filter(entry => entry.billId).map(entry => entry.billId)
  });
  const describedEntries = entries.map(entry => _objectSpread({
    entry
  }, describeEntry(entry, entry.billId ? store.companyBills.getBill(entry.billId) : null, addressById)));
  const handleDone = () => {
    const summary = summarizeUploadQueue(describedEntries);
    if (summary) toast(summary.tone, {
      title: summary.title,
      body: summary.lines
    });
    handleClose();
  };

  /** The original opens in the bill's own details, so this closes first; the uploads carry on behind it. */
  const handleViewOriginal = billId => {
    handleClose();
    onOpenBill === null || onOpenBill === void 0 || onOpenBill(billId);
  };
  return /*#__PURE__*/React.createElement(SimpleModal, {
    title: companyName ? "Upload bills \xB7 ".concat(companyName) : 'Upload bills',
    isOpen: isOpen,
    handleClose: handleClose
    // Without this the modal rebuilds its children on every render and the inputs lose focus.
    ,
    isMemoChildren: true,
    variant: "middle",
    leftButton: /*#__PURE__*/React.createElement(Button, {
      variant: "text",
      onClick: () => queue.cancelQueue(),
      disabled: !queue.hasEntries,
      'custom-attribute': 'bills-upload-bill-modal-cancel-queue'
    }, "Cancel queue"),
    rightButton: /*#__PURE__*/React.createElement(Button, {
      variant: "contained",
      onClick: handleDone,
      'custom-attribute': 'bills-upload-bill-modal-done'
    }, "Done")
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.notice
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "info",
    state: "info",
    fontSize: "small"
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, "Each file is extracted automatically. We auto-attach to the right property when we recognize the address or utility account number \u2014 anything unrecognized lands in ", /*#__PURE__*/React.createElement("strong", null, "Needs review"), ".")), isNoteOffered && !isLocked ? /*#__PURE__*/React.createElement(TextField, {
    className: classes.noteField,
    fullWidth: true,
    multiline: true,
    minRows: 2,
    maxRows: 5,
    label: "Note for our team (optional)",
    placeholder: "e.g. leave the solid waste off this month",
    helperText: "Goes with the files you drop next. Our team sees it with the bill.",
    value: note,
    onChange: event => setNote(event.target.value),
    inputProps: {
      maxLength: MAX_BILL_NOTE_LENGTH,
      'custom-attribute': 'bills-upload-bill-modal-note'
    }
  }) : null, isLocked ? /*#__PURE__*/React.createElement("div", {
    className: classes.notice
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "lock-locked",
    state: "info",
    fontSize: "small"
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, "Uploading bills here needs Plus or Autopilot, so no more files can be added.")) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "".concat(classes.dropzone, " ").concat(isDraggingOver ? classes.dropzoneActive : ''),
    onClick: () => {
      var _fileInputRef$current;
      return (_fileInputRef$current = fileInputRef.current) === null || _fileInputRef$current === void 0 ? void 0 : _fileInputRef$current.click();
    },
    onDragOver: event => {
      event.preventDefault();
      setIsDraggingOver(true);
    },
    onDragLeave: () => setIsDraggingOver(false),
    onDrop: handleDrop,
    'custom-attribute': 'bills-upload-bill-modal-dropzone'
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "upload",
    state: "grey"
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    className: classes.dropzoneTitle
  }, "Drop bills here, or ", /*#__PURE__*/React.createElement("span", {
    className: classes.dropzoneBrowse
  }, "browse")), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.dropzoneHint
  }, "PDF, PNG, JPG, GIF, WebP \xB7 up to ", MAX_BULK_UPLOAD_FILE_MB, " MB each \xB7 up to ", MAX_BULK_UPLOAD_FILES, " files at once")), /*#__PURE__*/React.createElement("input", {
    ref: fileInputRef,
    className: classes.hiddenInput,
    type: "file",
    multiple: true,
    accept: ACCEPTED_BILL_FILE_TYPES,
    onChange: handleBrowse
  })), entries.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.queueLabel
  }, entries.length, " file", entries.length === 1 ? '' : 's', " queued"), /*#__PURE__*/React.createElement("div", {
    className: classes.fileList
  }, describedEntries.map(_ref2 => {
    let entry = _ref2.entry,
      chip = _ref2.chip,
      detail = _ref2.detail,
      isUploading = _ref2.isUploading,
      originalBillId = _ref2.originalBillId;
    return /*#__PURE__*/React.createElement("div", {
      className: classes.fileRow,
      key: entry.id
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "file-text",
      state: "error"
    }), /*#__PURE__*/React.createElement("div", {
      className: classes.fileMain
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      className: classes.fileName,
      title: entry.fileName
    }, entry.fileName), isUploading ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(LinearProgress, {
      variant: "determinate",
      value: Math.round(entry.progress * 100),
      className: classes.fileProgress
    }), /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      className: classes.fileDetail
    }, "Uploading ".concat(Math.round(entry.progress * 100), "% \xB7 ").concat(formatMB(entry.sentMB), " / ").concat(formatMB(entry.fileSizeMB)))) : /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      className: classes.fileDetail
    }, "".concat(formatMB(entry.fileSizeMB)).concat(detail ? " \xB7 ".concat(detail) : ''), originalBillId && onOpenBill ? /*#__PURE__*/React.createElement(React.Fragment, null, ' · ', /*#__PURE__*/React.createElement(Button, {
      variant: "text",
      size: "small",
      className: classes.originalLink,
      onClick: () => handleViewOriginal(originalBillId),
      'custom-attribute': 'bills-upload-bill-modal-view-original'
    }, "View the original")) : null)), chip && /*#__PURE__*/React.createElement("span", {
      className: "".concat(classes.statusChip, " ").concat(classes["chip_".concat(chip.tone)])
    }, /*#__PURE__*/React.createElement("span", {
      className: classes.statusDot
    }), chip.label));
  }))), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.backgroundHint
  }, "You can close this \u2014 uploads continue in the background."));
});